import React, { useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Switch, TextField, Typography, Tooltip, IconButton,
    Box, Chip, Collapse, Select, MenuItem, FormControl, Grid
} from '@mui/material';
import {
    InfoOutlined as InfoIcon,
    KeyboardArrowDown as ExpandIcon,
    KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { RegionContextData, getRegionDisplayName } from '../../types';

const NON_NEGATIVE_MESSAGE = 'Value must be 0 or greater';

const isInvalidNonNegativeNumber = (value: unknown): boolean =>
    typeof value === 'number' && Number.isFinite(value) && value < 0;

interface RegionContextTableProps {
    data: RegionContextData[];
    onChange: (region: string, field: keyof RegionContextData, value: any) => void;
    onExcludeToggle: (region: string, excluded: boolean) => void;
    excludedRegions: string[];
    mode?: 'surplus' | 'shortage' | 'mixed' | 'reconciliation';
}

const RegionRow: React.FC<{
    row: RegionContextData;
    isExcluded: boolean;
    onToggle: (excluded: boolean) => void;
    onFieldChange: (field: keyof RegionContextData, value: any) => void;
    mode: 'surplus' | 'shortage' | 'mixed' | 'reconciliation';
}> = ({ row, isExcluded, onToggle, onFieldChange, mode }) => {
    const [open, setOpen] = useState(false);
    const isShortage = mode === 'shortage' || mode === 'mixed';
    const isSurplus = mode === 'surplus' || mode === 'mixed';
    const isMixed = mode === 'mixed';
    const isReconciliation = mode === 'reconciliation';

    const inputSx = {
        '& .MuiInputBase-input': {
            textAlign: 'right',
            fontSize: '13px',
            color: isExcluded ? 'text.disabled' : '#1a1a2e',
            fontWeight: 500,
            padding: '4px 0'
        },
        '& .MuiInput-underline:before': { borderBottomColor: '#e0e0e0' },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'primary.main' }
    };
    const invalid = (field: keyof RegionContextData) => !isExcluded && isInvalidNonNegativeNumber(row[field] as unknown);

    return (
        <>
            <TableRow
                sx={{
                    opacity: isExcluded ? 0.7 : 1,
                    bgcolor: isExcluded ? '#fcfcfc' : 'inherit',
                    transition: 'background-color 0.2s',
                    '& > *': { borderBottom: '1px solid #f0f0f0' }
                }}
            >
                <TableCell padding="checkbox">
                    <Tooltip title={isExcluded ? "Include Region" : "Exclude from Optimization"}>
                        <Switch
                            size="small"
                            checked={!isExcluded}
                            onChange={(e) => onToggle(!e.target.checked)}
                            sx={{
                                width: 36,
                                height: 20,
                                padding: 0,
                                '& .MuiSwitch-switchBase': {
                                    padding: 0,
                                    margin: '2px',
                                    transitionDuration: '300ms',
                                    '&.Mui-checked': {
                                        transform: 'translateX(16px)',
                                        color: '#fff',
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: 'primary.main',
                                            opacity: 1,
                                            border: 0,
                                        },
                                    },
                                },
                                '& .MuiSwitch-thumb': {
                                    boxSizing: 'border-box',
                                    width: 16,
                                    height: 16,
                                },
                                '& .MuiSwitch-track': {
                                    borderRadius: 10,
                                    backgroundColor: '#e9e9ea',
                                    opacity: 1,
                                    transition: 'background-color 500ms',
                                },
                            }}
                        />
                    </Tooltip>
                </TableCell>
                <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: isExcluded ? 'text.disabled' : '#1a1a2e' }}>
                            {getRegionDisplayName(row.region)}
                        </Typography>
                        {isExcluded ? (
                            <Chip label="EXCLUDED" size="small" variant="filled" sx={{ fontSize: '9px', height: 18, bgcolor: '#ffebee', color: '#d32f2f', fontWeight: 700 }} />
                        ) : (
                            <Chip label="ACTIVE" size="small" variant="filled" sx={{ fontSize: '9px', height: 18, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                        )}
                    </Box>
                </TableCell>
                <TableCell align="right">
                    {isShortage && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Select
                                size="small"
                                variant="standard"
                                value={row.strategic_priority || 'Normal'}
                                onChange={(e) => onFieldChange('strategic_priority', e.target.value)}
                                disabled={isExcluded}
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    width: 90,
                                    color: row.strategic_priority === 'Urgent' ? '#d32f2f' : row.strategic_priority === 'High' ? 'primary.main' : 'inherit'
                                }}
                            >
                                <MenuItem value="Normal">Normal</MenuItem>
                                <MenuItem value="High">High</MenuItem>
                                <MenuItem value="Urgent">Urgent</MenuItem>
                            </Select>
                            {row.priority_reasoning && (
                                <Tooltip
                                    title={
                                        <Box sx={{ p: 0.5 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                                Strategic Rationale:
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: '11px', lineHeight: 1.4 }}>
                                                {row.priority_reasoning}
                                            </Typography>
                                        </Box>
                                    }
                                    placement="right"
                                    arrow
                                >
                                    <InfoIcon sx={{ fontSize: 16, color: 'primary.main', cursor: 'help', opacity: 0.7 }} />
                                </Tooltip>
                            )}
                        </Box>
                    )}
                    {isSurplus && (
                        <TextField
                            variant="standard"
                            type="number"
                            size="small"
                            value={row.velocity_score ?? 0}
                            onChange={(e) => onFieldChange('velocity_score', parseFloat(e.target.value))}
                            disabled={isExcluded}
                            error={invalid('velocity_score')}
                            inputProps={{ min: 0 }}
                            sx={{ ...inputSx, width: 60 }}
                        />
                    )}
                </TableCell>
                <TableCell align="right">
                    <TextField
                        variant="standard"
                        type="number"
                        size="small"
                        value={row.capacity ?? 0}
                        onChange={(e) => onFieldChange('capacity', parseInt(e.target.value))}
                        disabled={isExcluded}
                        error={invalid('capacity')}
                        inputProps={{ min: 0 }}
                        sx={{ ...inputSx, width: 70 }}
                    />
                </TableCell>
                {isSurplus && (
                    <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary', mr: 0.5 }}>$</Typography>
                            <TextField
                                variant="standard"
                                type="number"
                                size="small"
                                value={row.transport_cost ?? 0}
                                onChange={(e) => onFieldChange('transport_cost', parseFloat(e.target.value))}
                                disabled={isExcluded}
                                error={invalid('transport_cost')}
                                inputProps={{ min: 0 }}
                                sx={{ ...inputSx, width: 70 }}
                            />
                        </Box>
                    </TableCell>
                )}
                <TableCell align="right">
                    <IconButton size="small" onClick={() => setOpen(!open)} sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                        <ExpandIcon />
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: open ? '1px solid #f0f0f0' : 'none' }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, p: 2.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #edf2f7' }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, mb: 2, display: 'block', color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Advanced Regional Parameters
                            </Typography>
                            <Grid container spacing={4}>
                                <Grid item xs={3}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        {isShortage ? 'Regional Demand (Optimizer)' : 'Forecasted Demand'}
                                    </Typography>
                                    {isShortage ? (
                                        <TextField
                                            fullWidth variant="outlined" size="small" type="number"
                                            value={row.regional_demand ?? 0}
                                            onChange={(e) => onFieldChange('regional_demand', parseInt(e.target.value))}
                                            disabled={isExcluded}
                                            error={invalid('regional_demand')}
                                            helperText={invalid('regional_demand') ? NON_NEGATIVE_MESSAGE : ' '}
                                            inputProps={{ min: 0 }}
                                            sx={{ mt: 1, bgcolor: 'white' }}
                                        />
                                    ) : (
                                        <TextField
                                            fullWidth variant="outlined" size="small" type="number"
                                            value={row.forecasted_demand ?? 0}
                                            onChange={(e) => onFieldChange('forecasted_demand', parseInt(e.target.value))}
                                            disabled={isExcluded}
                                            error={invalid('forecasted_demand')}
                                            helperText={invalid('forecasted_demand') ? NON_NEGATIVE_MESSAGE : ' '}
                                            inputProps={{ min: 0 }}
                                            sx={{ mt: 1, bgcolor: 'white' }}
                                        />
                                    )}
                                </Grid>
                                {isShortage && (
                                    <Grid item xs={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Forecast Demand (Context)</Typography>
                                        <Box sx={{ mt: 1.5, p: 0.8, bgcolor: '#edf2f7', borderRadius: 1, textAlign: 'center' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#2d3748' }}>
                                                {row.forecasted_demand ?? 0} units
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                                {(isSurplus || isMixed) && (
                                    <>
                                        <Grid item xs={3}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Holding Cost/Day ($)</Typography>
                                            <TextField
                                                fullWidth variant="outlined" size="small" type="number"
                                                value={row.holding_cost_per_day ?? 0}
                                                onChange={(e) => onFieldChange('holding_cost_per_day', parseFloat(e.target.value))}
                                                disabled={isExcluded}
                                                error={invalid('holding_cost_per_day')}
                                                helperText={invalid('holding_cost_per_day') ? NON_NEGATIVE_MESSAGE : ' '}
                                                inputProps={{ min: 0 }}
                                                sx={{ mt: 1, bgcolor: 'white' }}
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Unit Incentive ($)</Typography>
                                            <TextField
                                                fullWidth variant="outlined" size="small" type="number"
                                                value={row.incentive_amount ?? 0}
                                                onChange={(e) => onFieldChange('incentive_amount', parseFloat(e.target.value))}
                                                disabled={isExcluded}
                                                error={invalid('incentive_amount')}
                                                helperText={invalid('incentive_amount') ? NON_NEGATIVE_MESSAGE : ' '}
                                                inputProps={{ min: 0 }}
                                                sx={{ mt: 1, bgcolor: 'white' }}
                                            />
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={3}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Units Sold (30d Baseline)</Typography>
                                    <Box sx={{ mt: 1.5, p: 0.8, bgcolor: '#edf2f7', borderRadius: 1, textAlign: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#2d3748' }}>
                                            {row.units_sold_last_30d ?? 0} units
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const RegionContextTable: React.FC<RegionContextTableProps> = ({
    data, onChange, onExcludeToggle, excludedRegions, mode = 'surplus'
}) => {
    const isShortage = mode === 'shortage' || mode === 'mixed';
    const isSurplus = mode === 'surplus' || mode === 'mixed';
    const isMixed = mode === 'mixed';

    return (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Table size="small">
                <TableHead sx={{ bgcolor: '#f5f7f9' }}>
                    <TableRow>
                        <TableCell width={50}></TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#546e7a' }}>Region</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#546e7a' }}>
                            {isMixed ? 'Priority / Velocity' : isShortage ? 'Strategic Priority' : 'Velocity'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#546e7a' }}>Capacity</TableCell>
                        {isSurplus && (
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#546e7a' }}>Transport $</TableCell>
                        )}
                        <TableCell width={50}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((row) => (
                        <RegionRow
                            key={row.region}
                            row={row}
                            isExcluded={excludedRegions.includes(row.region)}
                            onToggle={(excl) => onExcludeToggle(row.region, excl)}
                            onFieldChange={(field, val) => onChange(row.region, field, val)}
                            mode={mode}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default RegionContextTable;
